/**
 * The Phone page's pure rules: what End call says when the others stay on, and how a leg of the
 * signed-in user's history reads on the page. Pure, for `test/phone-page.test.ts`.
 */
import { placedBy } from "@/lib/calls";
import type { DialPath } from "@/lib/manual-call";
import { isPstnNumber, sameNumber, toE164 } from "@/lib/phone-number";
import type {
  AgentLine, Cdr, CdrSummary, Colleague, MissedCall, SoftphoneCredentials,
} from "@/lib/types";
import { formatDuration } from "@/lib/utils";

/**
 * What the red button does. Before the call is up it cancels it. Once two other people are on
 * the call with the rep, hanging up leaves them talking to each other (ADR-032: a room with two
 * members left goes on), so the button says so rather than promising to end a call it will not.
 *
 * On a supervisor's listen-in (`supervising`) it is not a hang-up at all to them: the rep's call
 * goes on without them, so it says what it does, "Stop supervising", from the moment it rings.
 */
export function endCallLabel(up: boolean, othersOnCall: number, supervising = false): string {
  if (supervising) return "Stop supervising";
  if (!up) return "Cancel call";
  return othersOnCall >= 2 ? "Leave the call" : "End call";
}

/** The number a rep's calls come from and go to, when it is more than their extension. */
export function ownNumber(
  credentials: Pick<SoftphoneCredentials, "extension" | "caller_number" | "number_alias"> | null,
): string | null {
  if (!credentials) return null;
  const number = credentials.caller_number?.trim();
  if (number && number !== credentials.extension && number !== credentials.number_alias) return number;
  return null;
}

/** The colleague a call is with, when the far end is one of the firm's extensions. */
export function colleagueFor(
  remote: string,
  colleagues: readonly Colleague[] | undefined,
): Colleague | null {
  return colleagues?.find((c) => c.extension === remote) ?? null;
}

/** Today's calls and minutes on the phone, from the history's totals. Zero until they load. */
export function daySummary(summary: CdrSummary | undefined): { calls: number; minutes: number } {
  return {
    calls: summary?.calls ?? 0,
    minutes: Math.round((summary?.talk_seconds ?? 0) / 60),
  };
}

/**
 * One leg of the signed-in user's history in a sentence: "Talked 6:02 · they called you",
 * "Missed · rang 24 s", "No answer · rang 30 s". Which side placed it comes
 * from `placedBy`, the reader's own split, never the switch's direction.
 */
export function legText(
  leg: Pick<
    Cdr,
    "caller_number" | "callee_number" | "answer_time" | "billsec_seconds" | "duration_seconds"
  > &
    Partial<Pick<Cdr, "kind" | "dial_mode">>,
  mine: readonly string[],
): string {
  const by = placedBy(leg, mine);
  const who =
    by === "you"
      ? "you called them"
      : by === "dialer"
        ? "the dialer called them for you"
        : by === "them"
          ? "they called you"
          : null;
  if (leg.answer_time) {
    return `Talked ${formatDuration(leg.billsec_seconds)}${who ? ` · ${who}` : ""}`;
  }
  const rang = leg.duration_seconds > 0 ? ` · rang ${Math.round(leg.duration_seconds)} s` : "";
  // A dialer call my phone did not take is one I missed, though it counts as placed: the lead
  // had answered, and my phone rang with them and was not picked up.
  return by === "you" && leg.kind !== "outbound" ? `No answer${rang}` : `Missed${rang}`;
}

/**
 * One missed call in a sentence, for the Phone page's list: what happened on the rep's side,
 * then where the call came from, then what became of the caller.
 *
 *   "Rang you 28 s · Tern Harbor · Marcus Lee took it"
 *   "You were unavailable · your number on Tern Harbor · nobody took it"
 *   "Your phone was offline · your number"
 *   "Rang you 10 s"                                     (a colleague dialing the extension)
 *
 * "Unavailable" is the ACD's word: it did not ring the rep, because they were on a break, signed
 * out or on another call, and the record does not say which. An ABANDONED outcome is the caller
 * giving up on an inbound call and the dialer giving up on an outbound one, so the two read
 * differently.
 */
export function missedCallText(
  call: Pick<
    MissedCall,
    | "reason"
    | "rang_seconds"
    | "own_number"
    | "campaign_name"
    | "queue_name"
    | "outcome"
    | "direction"
    | "taken_by"
    | "taken_by_name"
  >,
  asRep = false,
): string {
  const parts: string[] = [];
  if (call.reason === "no_answer") {
    parts.push(call.rang_seconds ? `Rang you ${Math.round(call.rang_seconds)} s` : "Rang you");
  } else if (call.reason === "offline") {
    parts.push("Your phone was offline");
  } else {
    parts.push("You were unavailable");
  }
  const line = asRep ? null : (call.campaign_name ?? call.queue_name);
  if (call.own_number) parts.push(line ? `your number on ${line}` : "your number");
  else if (line) parts.push(line);
  if (call.taken_by) {
    parts.push(`${call.taken_by_name ?? `ext. ${call.taken_by}`} took it`);
  } else if (call.outcome === "ABANDONED") {
    parts.push(call.direction === "outbound" ? "the dialer gave up" : "they hung up");
  } else if (call.outcome === "TIMEOUT") {
    parts.push("nobody took it");
  }
  return parts.join(" · ");
}

/**
 * Who a missed call was from: the name the switch carried, the colleague the extension belongs
 * to, the number, or "Unknown" for a caller who withheld theirs.
 */
export function missedCallWho(
  call: Pick<MissedCall, "caller_number" | "caller_name">,
  colleagues: readonly Colleague[] | undefined,
  format: (number: string) => string,
): string {
  if (call.caller_name) return call.caller_name;
  if (!call.caller_number) return "Unknown";
  return colleagueFor(call.caller_number, colleagues)?.name ?? format(call.caller_number);
}

/**
 * The number shown beside that name, or null when the name IS the number, or there is none. A
 * name alone does not place a caller — two Danas, a colleague's name on an outside number the
 * switch happened to know — and the number is what a rep reads off the card to call or look up.
 */
export function missedCallNumber(
  call: Pick<MissedCall, "caller_number" | "caller_name">,
  colleagues: readonly Colleague[] | undefined,
  format: (number: string) => string,
): string | null {
  if (!call.caller_number) return null;
  const number = format(call.caller_number);
  return missedCallWho(call, colleagues, format) === number ? null : number;
}

/**
 * Where a missed call's Call back goes (ADR-047): through the campaign the call came in on, so
 * the call is a campaign call again — with Send to a closer, the roster and the rest — and
 * never a plain call from the phone in its place, which is the call the client found had no
 * transfer on it.
 *
 * `campaign`: the rep can call through that campaign now — Ready, on a break or in wrap-up on
 * it (ADR-068) — and the call goes through it. `blocked`: the call came in on a campaign the rep
 * is not signed in to or is on a call on (or that is gone), and the button says so instead of
 * quietly dialing another way. `default`: the call came in on no
 * campaign — a colleague, the rep's direct number — so it takes the Dial box's own path: an
 * extension rings the colleague directly, an outside number goes through the campaign the rep
 * chose, or from the phone when they can call through none.
 */
export type CallBackPath =
  | { via: "campaign"; campaign: AgentLine }
  | { via: "default" }
  | { via: "blocked"; reason: string };

export function callBackPath(
  call: Pick<MissedCall, "campaign_id" | "campaign_name" | "queue_name">,
  path: DialPath,
  asRep = false,
): CallBackPath {
  if (path.via === "phone" && path.reason === "extension") return { via: "default" };
  if (!call.campaign_id) return { via: "default" };
  const campaign =
    path.via === "campaign"
      ? path.campaigns.find((c) => c.campaign_id === call.campaign_id)
      : undefined;
  if (campaign) return { via: "campaign", campaign };
  if (asRep) return { via: "blocked", reason: "Go Ready to call back." };
  const name = call.campaign_name ?? call.queue_name ?? "that campaign";
  return { via: "blocked", reason: `Go Ready on ${name} to call back through it.` };
}

/** The Call back button's tooltip: where the call goes, or why it will not go yet. */
export function callBackTitle(back: CallBackPath, asRep = false): string {
  switch (back.via) {
    case "campaign":
      if (asRep) return "Call back";
      return `Call back through ${back.campaign.campaign_name ?? back.campaign.name}`;
    case "blocked":
      return back.reason;
    case "default":
      return "Call back";
  }
}

/**
 * The time of the newest call in the list: how far a rep who has the list open has seen. The
 * server's own times and never the browser's clock, which may be minutes off the server's and
 * would then count a call the rep has just read as one they have not. Null for an empty list.
 */
export function newestMissed(calls: readonly Pick<MissedCall, "at">[]): string | null {
  let newest: string | null = null;
  let newestAt = Number.NEGATIVE_INFINITY;
  for (const call of calls) {
    const at = Date.parse(call.at);
    if (at > newestAt) {
      newest = call.at;
      newestAt = at;
    }
  }
  return newest;
}

/**
 * How many missed calls the rep has not looked at, for the count on the rail's button: the ones
 * that came after the newest call that was in the list when they last had it open (`seen`,
 * `newestMissed` at that moment). With no mark, or one that cannot be read, every call in the
 * list counts: a rep is told about a call twice sooner than not at all.
 */
export function unseenMissed(
  calls: readonly Pick<MissedCall, "at">[],
  seen: string | null,
): number {
  const mark = seen === null ? Number.NaN : Date.parse(seen);
  if (Number.isNaN(mark)) return calls.length;
  return calls.filter((call) => Date.parse(call.at) > mark).length;
}

/** That count as the button's badge says it: "3", "9+" past nine, and nothing for none. */
export function missedBadge(count: number): string | null {
  if (count <= 0) return null;
  return count > 9 ? "9+" : String(count);
}

/**
 * The digits the contacts search is asked for, to learn who an outside number is (ACL-483): the
 * last ten of a US or Canadian number, so a record with or without its +1 is found either way,
 * and every digit of an international one. Null for anything that no contact can be: an
 * extension, a room, a hidden caller.
 */
export function callerSearchDigits(remote: string | null | undefined): string | null {
  // An outside number by the phone's one rule (`lib/phone-number.ts`), in E.164.
  const number = toE164(remote);
  if (number === null) return null;
  return isPstnNumber(number) ? number.slice(-10) : number.slice(1);
}

/**
 * The contact an outside number belongs to, among the rows the search found for it: the newest
 * one with this very number, the rule the ACD's own lookup keeps (`leads_resolve.lead_by_phone` in
 * core-api). The search matches the digits anywhere in a phone, so a row whose number only
 * contains them is somebody else.
 */
export function callerContact<T extends { phone: string; created_at: string }>(
  rows: readonly T[] | undefined,
  remote: string,
): T | null {
  let found: T | null = null;
  for (const row of rows ?? []) {
    if (!sameNumber(row.phone, remote)) continue;
    if (found === null || Date.parse(row.created_at) > Date.parse(found.created_at)) found = row;
  }
  return found;
}

/**
 * How one of the signed-in user's earlier calls with a number went, for its mark on the caller's
 * card: a conversation, by which side placed it, or a call that was not answered, worded as
 * `legText` words it ("Missed" when it rang this phone, "No answer" when the rep called out).
 */
export function legResult(
  leg: Pick<Cdr, "caller_number" | "callee_number" | "answer_time"> &
    Partial<Pick<Cdr, "kind" | "dial_mode">>,
  mine: readonly string[],
): "talked_in" | "talked_out" | "missed" | "no_answer" {
  const by = placedBy(leg, mine);
  if (leg.answer_time) return by === "them" ? "talked_in" : "talked_out";
  return by === "you" && leg.kind !== "outbound" ? "no_answer" : "missed";
}

/** "3 calls before this one", "1 call before this one", or null with none. */
export function callsBeforeText(count: number): string | null {
  if (count <= 0) return null;
  return `${count} ${count === 1 ? "call" : "calls"} before this one`;
}

/**
 * How far from the rail one of its lists opens ("My calls today", "Missed calls"), in pixels, so
 * it sits beside whatever the rail already has open and never over it. Alone it is 8 from the
 * rail, as the phone's own popover is. The phone's popover and the voicemail suggestion are each
 * 320 wide with an 8 gap, so each one that is open pushes it 328 further out
 * (`voicemail-suggestion.tsx` holds the same two numbers).
 */
export function railOffset(
  { phoneOpen, suggestionOpen }: { phoneOpen: boolean; suggestionOpen: boolean },
): number {
  return 8 + 328 * (Number(phoneOpen) + Number(suggestionOpen));
}

/** The time a call started, as the day's list says it: "9:51 AM", or the date when it was not
 *  today. Nothing for a leg with no start. */
export function clockTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  if (at.toDateString() === now.toDateString()) {
    return at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  return at.toLocaleDateString([], { month: "short", day: "numeric" });
}
